import React, { useState, useEffect } from 'react';
import { 
  SparklesIcon,
  TrophyIcon,
  StarIcon,
  FireIcon,
  GiftIcon,
  XMarkIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

// 알림 타입별 아이콘 반환 함수
const getNotificationIcon = (type) => {
  switch (type) {
    case 'points':
      return <StarIcon className="w-5 h-5" />;
    case 'levelup':
      return <TrophyIcon className="w-5 h-5" />;
    case 'badge':
      return <SparklesIcon className="w-5 h-5" />;
    default:
      return <GiftIcon className="w-5 h-5" />;
  }
};

const GamificationNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // 포인트 획득 이벤트 리스너
    const handlePointsEarned = (event) => {
      const data = event.detail;
      addNotification({
        id: Date.now(),
        type: 'points',
        title: data.message,
        subtitle: `+${data.points} 포인트`,
        icon: data.levelUp ? '🎉' : '⭐',
        color: data.levelUp ? 'from-purple-500 to-pink-500' : 'from-blue-500 to-cyan-500',
        priority: data.levelUp ? 'high' : 'normal',
        data: data
      });

      // 레벨업 시 추가 알림
      if (data.levelUp) {
        setTimeout(() => {
          addNotification({
            id: Date.now() + 1,
            type: 'levelup',
            title: '레벨업! 🎉',
            subtitle: `${data.newLevel.title} (Lv.${data.newLevel.level})`,
            icon: data.newLevel.icon,
            color: 'from-yellow-400 to-orange-500',
            priority: 'high',
            data: data.newLevel
          });
        }, 500);
      }
    };

    // 배지 획득 이벤트 리스너
    const handleBadgesEarned = (event) => {
      const badges = event.detail;
      badges.forEach((badge, index) => {
        setTimeout(() => {
          addNotification({
            id: Date.now() + index,
            type: 'badge',
            title: `배지 획득! ${badge.icon}`,
            subtitle: badge.name,
            description: badge.description,
            icon: badge.icon,
            color: getBadgeColor(badge.rarity),
            priority: badge.rarity === 'legendary' ? 'high' : 'normal',
            data: badge
          });
        }, index * 300);
      });
    };

    window.addEventListener('gamificationpointsEarned', handlePointsEarned);
    window.addEventListener('gamificationbadgesEarned', handleBadgesEarned);

    return () => {
      window.removeEventListener('gamificationpointsEarned', handlePointsEarned);
      window.removeEventListener('gamificationbadgesEarned', handleBadgesEarned);
    };
  }, []);

  const getBadgeColor = (rarity) => {
    const colors = {
      common: 'from-gray-400 to-gray-600',
      uncommon: 'from-green-400 to-green-600',
      rare: 'from-blue-400 to-blue-600',
      epic: 'from-purple-400 to-purple-600',
      legendary: 'from-yellow-400 to-orange-500'
    };
    return colors[rarity] || colors.common;
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    
    // 자동 제거 (우선순위에 따라 시간 조절)
    const duration = notification.priority === 'high' ? 6000 : 4000;
    setTimeout(() => {
      removeNotification(notification.id);
    }, duration);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (notifications.length === 0) return null;

  return (
    <>
      <AnimationStyles />
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
        {/* 최소화 토글 버튼 */}
      {notifications.length > 0 && (
        <div className="flex justify-end mb-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-full p-2 text-white hover:bg-white/30 transition-colors"
            title={isMinimized ? '알림 펼치기' : '알림 접기'}
          >
            <ChevronUpIcon className={`w-4 h-4 transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}

      {/* 알림 목록 */}
      <div className={`space-y-2 transition-all duration-300 ${isMinimized ? 'max-h-0 overflow-hidden' : 'max-h-96 overflow-y-auto'}`}>
        {notifications.map((notification) => (
          <NotificationCard 
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
      </div>
    </>
  );
};

const NotificationCard = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // 등장 애니메이션
    setTimeout(() => setIsVisible(true), 50);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(), 300);
  };

  const getAnimationClass = () => {
    if (isExiting) return 'animate-slide-out-right opacity-0';
    if (isVisible) return 'animate-slide-in-right';
    return 'opacity-0 translate-x-full';
  };

  return (
    <div className={`transform transition-all duration-300 ${getAnimationClass()}`}>
      <div className={`bg-gradient-to-r ${notification.color} p-1 rounded-xl shadow-lg`}>
        <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              {/* 아이콘 */}
              <div className={`w-10 h-10 bg-gradient-to-r ${notification.color} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                {notification.type === 'badge' ? (
                  <span className="text-lg">{notification.icon}</span>
                ) : (
                  getNotificationIcon(notification.type)
                )}
              </div>

              {/* 내용 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h4 className="text-sm font-bold text-gray-900 truncate">
                    {notification.title}
                  </h4>
                  {notification.priority === 'high' && (
                    <FireIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mt-1">
                  {notification.subtitle}
                </p>
                
                {notification.description && (
                  <p className="text-xs text-gray-500 mt-1">
                    {notification.description}
                  </p>
                )}

                {/* 레벨업 특별 표시 */}
                {notification.type === 'levelup' && notification.data.levelUpBonus && (
                  <div className="mt-2 text-xs text-purple-600 font-medium">
                    보너스: +{notification.data.levelUpBonus} 포인트
                  </div>
                )}

                {/* 배지 희귀도 표시 */}
                {notification.type === 'badge' && (
                  <div className="mt-2">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      notification.data.rarity === 'legendary' ? 'bg-yellow-100 text-yellow-800' :
                      notification.data.rarity === 'epic' ? 'bg-purple-100 text-purple-800' :
                      notification.data.rarity === 'rare' ? 'bg-blue-100 text-blue-800' :
                      notification.data.rarity === 'uncommon' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {notification.data.rarity.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* 닫기 버튼 */}
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* 진행률 바 (레벨업 시) */}
          {notification.type === 'levelup' && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>레벨 {notification.data.level}</span>
                <span>{notification.data.title}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: '100%' }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 사용자 정의 CSS 애니메이션을 위한 스타일 컴포넌트
const AnimationStyles = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slide-in-right {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes slide-out-right {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
      
      .animate-slide-in-right {
        animation: slide-in-right 0.3s ease-out;
      }
      
      .animate-slide-out-right {
        animation: slide-out-right 0.3s ease-in;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return null;
};

export default GamificationNotifications;